import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Experience } from '../types/Experience';
import { ExperienceBubble } from './ExperienceBubble';
interface ExperienceBubbleClusterProps {
  experiences: Experience[];
  selectedExperienceId: string | null;
  onSelectExperience: (id: string) => void;
}
interface BubblePosition {
  x: number;
  y: number;
}
// Generate packed bubble layout similar to Apple Watch / smartwatch apps
function generatePackedBubbleLayout(experiences: Experience[], bubbleSize: number = 110): BubblePosition[] {
  const positions: BubblePosition[] = [];
  const spacing = bubbleSize + 10; // Horizontal spacing for 5px minimum margin
  const verticalSpacing = 113; // Vertical spacing: 110px bubble height + 3px margin
  const cols = 5;
  const rows = Math.ceil(experiences.length / cols);
  experiences.forEach((_, index) => {
    const row = Math.floor(index / cols);
    const col = index % cols;
    const xOffset = row % 2 * (spacing / 2);
    const x = col * spacing + xOffset - (cols - 1) * spacing / 2;
    const y = row * verticalSpacing - (rows - 1) * verticalSpacing / 2;
    positions.push({
      x,
      y
    });
  });
  return positions;
}
export function ExperienceBubbleCluster({
  experiences,
  selectedExperienceId,
  onSelectExperience
}: ExperienceBubbleClusterProps) {
  const [offset, setOffset] = useState({
    x: 0,
    y: 0
  });
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const dragStartRef = useRef({
    x: 0,
    y: 0
  });
  const offsetStartRef = useRef({
    x: 0,
    y: 0
  });
  const dragDistanceRef = useRef(0);
  const [bubblePositions, setBubblePositions] = useState<BubblePosition[]>([]);
  useEffect(() => {
    const positions = generatePackedBubbleLayout(experiences, 110);
    setBubblePositions(positions);
  }, [experiences]);
  const handleMouseDown = (e: React.MouseEvent) => {
    dragStartRef.current = {
      x: e.clientX,
      y: e.clientY
    };
    offsetStartRef.current = {
      ...offset
    };
    dragDistanceRef.current = 0;
    setIsDragging(false);
  };
  const handleMouseMove = (e: React.MouseEvent) => {
    if (dragStartRef.current.x === 0) return;
    const dx = e.clientX - dragStartRef.current.x;
    const dy = e.clientY - dragStartRef.current.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    dragDistanceRef.current = distance;
    if (distance > 5) {
      setIsDragging(true);
      setOffset({
        x: offsetStartRef.current.x + dx,
        y: offsetStartRef.current.y + dy
      });
    }
  };
  const handleMouseUp = () => {
    dragStartRef.current = {
      x: 0,
      y: 0
    };
    setIsDragging(false);
    // Reset drag distance after a brief delay to allow click handlers to check it
    setTimeout(() => {
      dragDistanceRef.current = 0;
    }, 100);
  };
  // Touch events for mobile
  const handleTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    dragStartRef.current = {
      x: touch.clientX,
      y: touch.clientY
    };
    offsetStartRef.current = {
      ...offset
    };
    dragDistanceRef.current = 0;
    setIsDragging(false);
  };
  const handleTouchMove = (e: React.TouchEvent) => {
    if (dragStartRef.current.x === 0) return;
    const touch = e.touches[0];
    const dx = touch.clientX - dragStartRef.current.x;
    const dy = touch.clientY - dragStartRef.current.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    dragDistanceRef.current = distance;
    if (distance > 5) {
      setIsDragging(true);
      setOffset({
        x: offsetStartRef.current.x + dx,
        y: offsetStartRef.current.y + dy
      });
    }
  };
  const handleTouchEnd = () => {
    dragStartRef.current = {
      x: 0,
      y: 0
    };
    setIsDragging(false);
    // Reset drag distance after a brief delay to allow click handlers to check it
    setTimeout(() => {
      dragDistanceRef.current = 0;
    }, 100);
  };
  useEffect(() => {
    if (selectedExperienceId && bubblePositions.length > 0) {
      const selectedIndex = experiences.findIndex(exp => exp.id === selectedExperienceId);
      if (selectedIndex !== -1) {
        const position = bubblePositions[selectedIndex];
        setOffset({
          x: -position.x,
          y: -position.y
        });
      }
    }
  }, [selectedExperienceId, bubblePositions, experiences]);
  const handleBubbleClick = (id: string) => {
    // Only open detail if user didn't drag (distance < 5px threshold)
    if (dragDistanceRef.current < 5) {
      onSelectExperience(id);
    }
  };
  return <div ref={containerRef} className="relative w-full h-full overflow-hidden" onMouseDown={handleMouseDown} onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp} onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd} style={{
    cursor: isDragging ? 'grabbing' : 'grab',
    background: '#F9FAFB'
  }} data-id="element-49">
      <motion.div className="absolute inset-0 bubble-cluster-content" animate={{
      x: offset.x,
      y: offset.y
    }} transition={{
      type: 'spring',
      stiffness: 160,
      damping: 22
    }} data-id="element-50">
        {bubblePositions.map((position, index) => {
        const experience = experiences[index];
        return <ExperienceBubble key={experience.id} experience={experience} isSelected={experience.id === selectedExperienceId} onClick={() => handleBubbleClick(experience.id)} style={{
          left: `calc(50% + ${position.x}px)`,
          top: `calc(50% + ${position.y}px)`,
          transform: 'translate(-50%, -50%)'
        }} data-id="element-51" />;
      })}
      </motion.div>
    </div>;
}